import React, { useEffect, useState } from 'react';
import { FiRefreshCw, FiX } from 'react-icons/fi';
import { applyUpdate } from '../../dry/pwa';

/**
 * VersionChecker Component
 *
 * Listens for the 'sw-update-available' custom event dispatched by DryPWA's
 * registerSW() onUpdate callback (see src/dry/pwa.ts). Shows an update
 * notification when a new SW version is waiting to activate.
 *
 * Replaces the old polling-based approach with event-driven detection.
 */
const VersionChecker: React.FC = () => {
  const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);

  useEffect(() => {
    const handler = () => setShowUpdatePrompt(true);
    window.addEventListener('sw-update-available', handler);
    return () => window.removeEventListener('sw-update-available', handler);
  }, []);

  const handleUpdate = () => {
    applyUpdate();
    setShowUpdatePrompt(false);
  };

  const handleDismiss = () => {
    setShowUpdatePrompt(false);
  };

  if (!showUpdatePrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-md">
      <div className="bg-blue-600 text-white rounded-lg shadow-2xl p-4 flex items-start gap-3">
        <div className="shrink-0">
          <FiRefreshCw className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold mb-1">Update Available</h3>
          <p className="text-sm text-blue-100 mb-3">
            A new version of ChecksAndTicks is available. Update now to get the latest features and improvements.
          </p>
          <div className="flex gap-2">
            <button
              onClick={handleUpdate}
              className="bg-white text-blue-600 px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-50 transition-colors"
            >
              Update Now
            </button>
            <button
              onClick={handleDismiss}
              className="bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-800 transition-colors"
            >
              Later
            </button>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="shrink-0 text-blue-200 hover:text-white transition-colors"
        >
          <FiX className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default VersionChecker;
